import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getAktivitaGscOverview,
  getAktivitaGscTrend,
  getAktivitaGscQueries,
  type AktivitaPeriod,
  type AktivitaCountry,
  type GscOverview,
  type GscTrend,
  type GscQueries,
  type GscQueryItem,
} from '../../api/rondo';
import { ArcSelect, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Card } from '../../components/arc/card/card';
import { Button } from '../../components/arc/button/button';
import { PageState } from '../../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import { TrendChart, fmtNum, fmtPct } from './aktivita-shared';
import './aktivita-drill.css';

type GscKpiKey = 'clicks' | 'impressions' | 'ctr' | 'position';
interface GscKpiDef {
  key: GscKpiKey;
  label: string;
  fmt: 'num' | 'pct2' | 'pos';
}
const TILES: GscKpiDef[] = [
  { key: 'clicks', label: 'Kliky', fmt: 'num' },
  { key: 'impressions', label: 'Imprese', fmt: 'num' },
  { key: 'ctr', label: 'CTR', fmt: 'pct2' },
  { key: 'position', label: 'Průměrná pozice', fmt: 'pos' },
];

function gscVal(v: number, fmt: GscKpiDef['fmt']): string {
  if (fmt === 'num') return fmtNum(v);
  if (fmt === 'pct2') return fmtPct(v, 2);
  return Number(v ?? 0).toFixed(1);
}

export function AktivitaVyhledavaniPage() {
  const [period, setPeriod] = useState<AktivitaPeriod>('month');
  const [country, setCountry] = useState<AktivitaCountry>('all');

  const [overview, setOverview] = useState<GscOverview | null>(null);
  const [trend, setTrend] = useState<GscTrend | null>(null);
  const [queries, setQueries] = useState<GscQueries | null>(null);
  const [tilesError, setTilesError] = useState<string | null>(null);
  const [qError, setQError] = useState<string | null>(null);
  const [qLoading, setQLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setTilesError(null);
    setQError(null);
    setQLoading(true);
    getAktivitaGscOverview({ period, country })
      .then((d) => !cancelled && setOverview(d))
      .catch((e: unknown) => !cancelled && setTilesError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
    getAktivitaGscTrend({ period, country })
      .then((d) => !cancelled && setTrend(d))
      .catch(() => {});
    getAktivitaGscQueries({ period, country, limit: 50 })
      .then((d) => !cancelled && setQueries(d))
      .catch((e: unknown) => !cancelled && setQError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setQLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, country]);

  const k = overview?.kpi;
  const tilesMeta = (() => {
    if (tilesError) return null;
    if (!overview?.meta?.last_synced_at) return 'GSC: žádná data (čekám na přístup service accountu k property)';
    return `GSC sync ${overview.meta.last_synced_at}`;
  })();

  const tp = trend?.points ?? [];
  const labels = tp.map((p) => p.date);
  const datasets = [
    {
      label: 'Kliky',
      data: tp.map((p) => Number(p.clicks ?? 0)),
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29,78,216,0.08)',
      yAxisID: 'y',
      fill: true,
    },
    {
      label: 'Imprese',
      data: tp.map((p) => Number(p.impressions ?? 0)),
      borderColor: '#137333',
      backgroundColor: 'rgba(19,115,51,0.05)',
      yAxisID: 'y2',
      fill: false,
    },
  ];
  const tFirst = tp[0];
  const tLast = tp[tp.length - 1];
  const trendRange = tFirst && tLast ? `${tFirst.date} – ${tLast.date} (${tp.length} dní)` : 'žádná data';
  type QueryRow = GscQueryItem & { rank: number } & Record<string, unknown>;
  const queryRows: QueryRow[] = (queries?.items ?? []).map((item, index) => ({ ...item, rank: index + 1 }));
  const queryColumns: DataColumn<QueryRow>[] = [
    { key: 'rank', label: '#', width: 65, numeric: true },
    { key: 'query', label: 'Dotaz', width: 550, render: (_, row) => <strong>{row.query}</strong> },
    { key: 'clicks', label: 'Kliky', width: 140, numeric: true, render: (_, row) => fmtNum(row.clicks) },
    { key: 'impressions', label: 'Imprese', width: 145, numeric: true, render: (_, row) => fmtNum(row.impressions) },
    { key: 'ctr', label: 'CTR', width: 130, numeric: true, render: (_, row) => fmtPct(row.ctr, 2) },
    { key: 'position', label: 'Pozice', width: 130, numeric: true, render: (_, row) => Number(row.position ?? 0).toFixed(1) },
  ];

  return (
    <section className="activity-custom-page activity-search-page rondo-arc-activity-drill p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>Vyhledávání</h1><p>Výkon dotazů v Google, prokliky a viditelnost ve výsledcích</p></div></header>
      <ArcToolbar className="rondo-arc-activity-toolbar" end={<small>{tilesMeta ?? '—'}</small>}>
        <ArcSelect label="Období" value={period} onChange={(value) => setPeriod(value as AktivitaPeriod)} options={[{ value: 'day', label: 'Den' }, { value: 'week', label: 'Týden' }, { value: 'month', label: 'Měsíc' }, { value: 'year', label: 'Rok' }]} />
        <ArcSelect label="Trh" value={country} onChange={(value) => setCountry(value as AktivitaCountry)} options={[{ value: 'all', label: 'Vše' }, { value: 'cz', label: 'CZ' }, { value: 'sk', label: 'SK' }, { value: 'ro', label: 'RO' }]} />
        <Button variant="ghost" size="sm" type="button" onClick={() => { setPeriod('month'); setCountry('all'); }}>Vyčistit</Button>
      </ArcToolbar>

      {tilesError ? <PageState title="Data GSC se nepodařilo načíst" message={tilesError} /> : (
        <div className="rondo-arc-activity-drill-kpis rondo-arc-activity-search-kpis">
          {TILES.map((t) => (
            <Card title={t.label} className="rondo-arc-activity-drill-kpi" key={t.key}><strong>{gscVal(Number(k?.[t.key] ?? 0), t.fmt)}</strong></Card>
          ))}
        </div>
      )}

      <Card title="Prokliky a imprese" description={trendRange} className="rondo-arc-activity-trend"><div>
          <TrendChart
            labels={labels}
            datasets={datasets}
            height={260}
            axes={[
              { id: 'y', position: 'left', title: 'Kliky', color: '#1d4ed8' },
              { id: 'y2', position: 'right', title: 'Imprese', color: '#137333', drawOnChartArea: false },
            ]}
          />
      </div></Card>

      <div className="rondo-arc-activity-pages-heading"><h2>Top vyhledávací dotazy</h2><span>{queryRows.length} dotazů</span></div>
      {qLoading ? <PageState title="Načítám dotazy" loading /> : qError ? <PageState title="Dotazy se nepodařilo načíst" message={qError} /> : <div className="rondo-arc-activity-pages-table rondo-arc-activity-search-table"><SortableDataTable rows={queryRows} columns={queryColumns} rowKey={(row) => `${row.query}-${row.rank}`} caption="Top vyhledávací dotazy" emptyMessage="Žádné dotazy (čekám na první GSC sync)" /></div>}
    </section>
  );
}
